/** Fire, water, earth, and wind. Bonds come from who is standing next to whom. */
export type ElementId = 'fire' | 'water' | 'earth' | 'wind';
export type BondTier = 'best' | 'good' | 'same' | 'bad';

export const ELEMENT_OF: Record<string, ElementId> = {
  pebble: 'earth',
  brook: 'water',
  cinder: 'fire',
  tempest: 'wind',
  pyre: 'fire',
  bombard: 'fire',
  siege: 'fire',
  frost: 'water',
  hexer: 'water',
  bastion: 'earth',
  grove: 'earth',
  bulwark: 'earth',
  vanguard: 'wind',
  skirmisher: 'wind',
  banner: 'wind',
  outrider: 'wind',
};

export const ELEMENT_STYLE: Record<ElementId, { name: string; color: string; deep: string; hot: string; blurb: string }> = {
  fire: { name: 'Fire', color: '#ff5a2a', deep: '#9a1e12', hot: '#ffd23a', blurb: 'Best beside Wind. Likes Earth. Suffers beside Water.' },
  water: { name: 'Water', color: '#3ec6ff', deep: '#1466b8', hot: '#f4fbff', blurb: 'Best beside Earth. Likes Wind. Suffers beside Fire.' },
  earth: { name: 'Earth', color: '#7dce55', deep: '#6a4324', hot: '#d7c07a', blurb: 'Best beside Water. Likes Fire. Suffers beside Wind.' },
  wind: { name: 'Wind', color: '#e7eef8', deep: '#8ea0b8', hot: '#ffffff', blurb: 'Best beside Fire. Likes Water. Suffers beside Earth.' },
};

export interface BondEffect {
  tier: BondTier;
  turretDamage: number;
  turretFire: number;
  minionDamage: number;
  minionHp: number;
  march: number;
}

const ZERO = { turretDamage: 0, turretFire: 0, minionDamage: 0, minionHp: 0, march: 0 };

/** Best pairs are the jackpot. Same element is a smaller resonance, not nothing. */
const PAIRS: Record<string, BondEffect> = {
  'earth-water': { tier: 'best', ...ZERO, turretFire: 0.4, minionHp: 0.4 },
  'fire-wind': { tier: 'best', ...ZERO, turretDamage: 0.4, minionDamage: 0.4, march: 0.4 },
  'water-wind': { tier: 'good', ...ZERO, turretDamage: 0.22, minionDamage: 0.22, minionHp: 0.22 },
  'earth-fire': { tier: 'good', ...ZERO, turretDamage: 0.22, minionHp: 0.22 },
  'fire-water': { tier: 'bad', ...ZERO, turretDamage: -0.24, minionDamage: -0.24 },
  'earth-wind': { tier: 'bad', ...ZERO, minionHp: -0.24, march: -0.24 },
};

const SAME: BondEffect = { tier: 'same', ...ZERO, turretDamage: 0.14, minionHp: 0.14 };

/** Extra gift for a straight line that contains every element. Added once, on top of the bonds. */
export const CONCORD_GIFT: BondEffect = { tier: 'best', ...ZERO, turretDamage: 0.14, minionHp: 0.14 };

export function bondEffect(a: ElementId, b: ElementId): BondEffect {
  if (a === b) return SAME;
  const key = [a, b].sort().join('-');
  return PAIRS[key] ?? SAME;
}

export function bondColor(tier: BondTier, element: ElementId): string {
  if (tier === 'best') return '#ffd23a';
  if (tier === 'bad') return '#ff5c7a';
  if (tier === 'same') return ELEMENT_STYLE[element].color;
  return '#fff4c8';
}

export function clampBond(delta: number): number {
  return Math.max(0.45, 1 + delta);
}
